import { useEffect, useRef, useState } from 'react';

/**
 * Keeps a loading flag `true` for at least `minMs` once it first becomes true,
 * so a fast response doesn't make the loading UI flash for a few milliseconds
 * (which reads as "nothing happened"). This is the right fix for "the spinner
 * is too fast to notice" — a short floor, NOT an artificial multi-second delay
 * that would make the app feel sluggish.
 *
 * Returns the debounced flag to drive the loading UI.
 */
export const useMinimumLoading = (isLoading: boolean, minMs = 500): boolean => {
  const [shown, setShown] = useState(isLoading);
  const startRef = useRef<number | null>(isLoading ? Date.now() : null);

  useEffect(() => {
    if (isLoading) {
      if (startRef.current === null) startRef.current = Date.now();
      setShown(true);
      return;
    }

    // Loading finished — hold the UI until at least minMs has elapsed since it
    // started, so quick responses still register visually.
    if (startRef.current === null) {
      setShown(false);
      return;
    }

    const elapsed = Date.now() - startRef.current;
    const remaining = Math.max(0, minMs - elapsed);

    if (remaining === 0) {
      startRef.current = null;
      setShown(false);
      return;
    }

    const timer = setTimeout(() => {
      startRef.current = null;
      setShown(false);
    }, remaining);

    return () => clearTimeout(timer);
  }, [isLoading, minMs]);

  return shown;
};

export default useMinimumLoading;
